import React from 'react';
import { createPortal } from 'react-dom';
import styles from './StanModal.module.css';
import { StanModalProps } from '../types';
import { StanButton } from '../StanButton/StanButton';
import { cn, createIconElement } from '@/lib';
import IcClose from '@/assets/icons/ic-x-close.svg';
import { useModalBehavior } from './useModalBehavior';

export const StanModal: React.FC<StanModalProps> = ({
    id,
    isOpen,
    title,
    children,
    className = '',
    primaryActionLabel,
    image,
    size = 'md',
    largeImage = false,
    secondaryActionLabel,
    primaryActionDisabled = false,
    secondaryActionDisabled = false,
    primaryActionLoading = false,
    primaryActionColor = 'primary',
    secondaryActionColor = 'primary',
    hideCloseButton = false,
    nonDismissible = false,
    footerNote,
    onClose,
    onPrimaryAction,
    onSecondaryAction,
}) => {
    const CloseIcon = createIconElement(IcClose, { className: styles.icon });

    const { modalRef, modalBumpAnimation, bumpAnimateOverlay } = useModalBehavior({
        isOpen,
        nonDismissible,
        onClose,
    });

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (nonDismissible) {
            bumpAnimateOverlay();
            return;
        }

        if (e.target === e.currentTarget) {
            onClose?.();
        }
    };

    return createPortal(
        <div
            className={cn(styles.modalOverlay, styles.modalFade, isOpen && styles.isOpen)}
            onClick={handleOverlayClick}
        >
            <div
                ref={modalRef}
                tabIndex={-1}
                className={cn(
                    styles.modalContainer,
                    styles.modalFade,
                    modalBumpAnimation && styles.modalScaleAnimation,
                    isOpen && styles.isOpen,
                    className,
                    size && styles[`modalContainer--${size}`]
                )}
                onClick={e => e.stopPropagation()}
            >
                {!hideCloseButton && (
                    <button className={`${styles.closeButton}`} onClick={onClose} aria-label='Close Modal'>
                        {CloseIcon}
                    </button>
                )}
                {image && (
                    <div
                        className={
                            largeImage ? `${styles.modalImage} ${styles['modalImage--large']}` : styles.modalImage
                        }
                    >
                        <img src={image} alt='' />
                    </div>
                )}

                {title && <h3 className={styles.modalTitle}>{title}</h3>}
                <div className={styles.modalContent}>
                    {children}
                    {(primaryActionLabel || secondaryActionLabel) && (
                        <div className={styles.modalActions}>
                            {primaryActionLabel && (
                                <StanButton
                                    name={`cta-button-modal-${id}`}
                                    color={primaryActionColor}
                                    loading={primaryActionLoading}
                                    disabled={primaryActionDisabled}
                                    onClick={onPrimaryAction}
                                >
                                    {primaryActionLabel}
                                </StanButton>
                            )}
                            {secondaryActionLabel && (
                                <StanButton
                                    name={`secondary-cta-button-modal-${id}`}
                                    variant='ghost'
                                    color={secondaryActionColor}
                                    disabled={secondaryActionDisabled}
                                    onClick={onSecondaryAction}
                                >
                                    {secondaryActionLabel}
                                </StanButton>
                            )}
                        </div>
                    )}
                </div>

                {footerNote && <div className={styles.footerNote}>{footerNote}</div>}
            </div>
        </div>,
        document.body
    );
};
